import React, { useContext, useState, useEffect, useLayoutEffect, useRef } from "react";
import styles from "./TemplatePreview.module.css";
import { AiOutlineClose } from "react-icons/ai";
import { MdOutlineMessage } from "react-icons/md";
import { FaCheck, FaChevronDown, FaChevronUp, FaWhatsapp } from "react-icons/fa";
import TemplatePreviewWhatsApp from "./TemplatePreviewWhatsApp";
import ActiveStoreContext from "../../../store/ActiveStoreContext";
import AuthContext from "../../../store/auth-context";
import { useParams } from "react-router";
import { BASE_URL_API } from "../../../References/URLs";
import { toast } from "react-toastify";
import ContentSelector from "../Assets/ContentSelector";

const TemplatePreview = ({ visible, onClose, template, onTemplateSelect, data, setData }) => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = (() => { try { return asCtx?.activeStore && asCtx.activeStore.trim() !== "" ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const { campaignIdentifier } = useParams()
    const [previewText, setPreviewText] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState(template);
    const [variables, setVariables] = useState([]);
    const [variableValues, setVariableValues] = useState({});
    const [footerVariables, setFooterVariables] = useState([])
    const [templateErrors, setTemplateErrors] = useState()
    const [isErrorOpen, setIsErrorOpen] = useState(false)
    const [isUpdatingFields, setIsUpdatingFields] = useState(false)
    const [isContentSelectorOpen, setIsContentSelectorOpen] = useState(false)
    // Tracks the last-saved (or freshly loaded) values so the action bar
    // can tell "nothing to update" apart from "there are unsaved changes" —
    // Select Template doesn't send varData itself, it relies on the backend
    // already having what Update Field last persisted, so it must stay
    // disabled while the fields are dirty.
    const [savedSnapshot, setSavedSnapshot] = useState({ headerContent: '', variableValues: {}, footerVariables: {} })

    const [headerData, setHeaderData] = useState({
        headerType: template?.headerType,
        headerContent: template?.headerContent,
    })

    // This modal's own fixed bottom action bar (Update Field / Select
    // Template) and the wizard shell's fixed bottom prev/next bar were
    // colliding on mobile: both are `position: fixed` at the same z-index,
    // and fixed elements escape their DOM ancestors' stacking context, so
    // this modal being visually "inside" the wizard didn't stop the shell's
    // later-in-DOM bar from painting on top of and intercepting touches
    // meant for this one. Telling the shell to hide its bar while this is
    // open — same self-registering event convention as layout:hideFab.
    useEffect(() => {
        if (!visible) return
        window.dispatchEvent(new CustomEvent('campaignWizard:hideMobileNav', { detail: { active: true } }))
        return () => {
            window.dispatchEvent(new CustomEvent('campaignWizard:hideMobileNav', { detail: { active: false } }))
        }
    }, [visible])

    // Reserves exactly enough space at the top of the scrolling content for
    // the fixed mobile header, measured for real rather than guessed as a
    // fixed pixel value — a guess is what caused content to start scrolling
    // out from underneath the header (its actual height didn't match the
    // hardcoded padding). Re-measures whenever the header's own content
    // could change height (the error banner expanding/collapsing) and on
    // window resize.
    const mobileHeaderRef = useRef(null)
    const [mobileHeaderHeight, setMobileHeaderHeight] = useState(0)

    useLayoutEffect(() => {
        const measure = () => {
            if (mobileHeaderRef.current) {
                setMobileHeaderHeight(mobileHeaderRef.current.offsetHeight)
            }
        }
        measure()
        window.addEventListener('resize', measure)
        return () => window.removeEventListener('resize', measure)
    }, [templateErrors, isErrorOpen])

    const [predefinedParams, setPredefinedParams] = useState([
        { identifier: 'storeName', label: 'Store Name', value: activeStore?.storeName },
        { identifier: 'storeAddress', label: 'Store Address', value: activeStore?.storeAddress },
        { identifier: 'storePrimaryPhone', label: 'Primary Phone', value: activeStore?.storePrimaryPhone },
        { identifier: 'storeSecondaryPhone', label: 'Secondary Phone', value: activeStore?.storeSecondaryPhone },
        { identifier: 'storeTertiaryPhone', label: 'Tertiary Phone', value: activeStore?.storeTertiaryPhone },
        { identifier: 'mapLink', label: 'Map Link', value: activeStore?.mapLink }
    ])

    // Utility to extract variables from content like {{1}}, {{2}}
    const extractVariables = (template) => {
        if (!template || typeof template.content !== 'string') return [];
        const matches = template.content.match(/{{\d+}}/g);
        return Array.isArray(matches) ? [...new Set(matches)] : [];
    };

    // Utility to replace variables with values
    const generatePreviewText = (content, values) => {
        if (!content) return "";
        return content.replace(/{{\d+}}/g, (match) => values[match] || match);
    };




    // On template change
    useEffect(() => {
        const extracted = extractVariables(template);
        setVariables(extracted);

        const initialValues = {};
        extracted.forEach((v) => (initialValues[v] = ""));
        setVariableValues(initialValues);

        setPreviewText(generatePreviewText(template?.content, initialValues));
        setSelectedTemplate(template);
        setSavedSnapshot({ headerContent: template?.headerContent || '', variableValues: initialValues, footerVariables: {} });
    }, [template]);

    // Handle input change
    const handleVariableChange = (variableKey, value) => {
        const updatedValues = { ...variableValues, [variableKey]: value };
        setVariableValues(updatedValues);
        setPreviewText(generatePreviewText(template?.content, updatedValues));
    };

    const handleFooterVariableChange = (variableKey, value) => {
        const updatedValues = { ...footerVariables, [variableKey]: value };
        setFooterVariables(updatedValues)
    };

    // Shared by handleSelectTemplate, submissionHandler, and the live
    // validation effect below — previously duplicated three times with a
    // bug baked into all three copies: a header entry was always included
    // (position 1), even for templates with no header at all, which
    // permanently failed validation for every headerless template.
    const buildVarData = () => {
        const varData = [];

        if (selectedTemplate?.headerType != null) {
            varData.push({ varType: 'header', position: 1, value: headerData?.headerContent });
        }

        Object.entries(variableValues).forEach(([key, value]) => {
            const position = parseInt(key.replace(/[{}]/g, '')); // Extract number from {{1}}
            varData.push({ varType: 'body', position, value });
        });

        if (Object.entries(footerVariables)?.length > 0) {
            Object.entries(footerVariables).forEach(([key, value]) => {
                const position = parseInt(key); // Extract number from {{1}}
                varData.push({ varType: 'footer', position, value });
            });
        }

        return varData;
    };

    function validateTemplateVars(input) {
        const errors = [];

        input.forEach(({ varType, position, value }, index) => {
            const isEmpty = value === "" || value === null || value === undefined;

            if (isEmpty) {
                errors.push({
                    error: `Missing value for ${varType} at position ${position}`,
                    index: index
                });
            }
        });

        if (errors.length > 0) {
            return {
                status: "error",
                message: "Some values are missing.",
                errors: errors
            };
        }

        return {
            status: "success",
            message: "All values are present."
        };
    }


    // Select Template handler
    const handleSelectTemplate = async () => {
        const varData = buildVarData();

        if(validateTemplateVars(varData)?.status==="success") {
            const response = await fetch(BASE_URL_API+"selectTemplateForCampaign",
            {
                method : "POST",
                body : JSON.stringify({
                    token : authCtx.token,
                    campaignIdentifier : campaignIdentifier,
                    templateData : template
                })
            })
            if(!response.ok) {
                toast.error(`Couldn't select the template. Please try again.`)
            } else {
                const respo = await response.json()
                if(respo.status==="success") {
                    toast(`Successfully selected the template!`)
                    onTemplateSelect()
                    onClose()
                } else {
                    if(respo.message==="tokenExpired") {
                        authCtx.logout()
                    } else {
                        toast.error(respo?.message || `Couldn't select the template. Please try again.`)
                    }
                }
            }
        } else {
            toast.warn(`You must fill all the parameters!`)
        }


    };

    const handleOverlayClick = () => onClose();
    const handleContentClick = (e) => e.stopPropagation();

    const variableSelectorHandler = (p, k) => {
        handleVariableChange(p, activeStore?.[k])
    }

    const footerSelectorHandler = (p, v) => {
        handleFooterVariableChange(p, activeStore?.[v])
    }

    const submissionHandler = () => {
        const varData = buildVarData();

        const setTemplateVariableForCampaign = async () => {
            setIsUpdatingFields(true)
            const response = await fetch(BASE_URL_API + "setTemplateVariableForCampaign",
                {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                        campaignIdentifier: campaignIdentifier,
                        templateIdentifier: template?.templateIdentifier,
                        varData: varData
                    })
                })

            if (!response.ok) {
                toast.error(`Couldn't save. Please try again.`)
            } else {
                const respo = await response.json()
                if (respo.status === "success") {
                    toast(`Successfully updated the data!`)
                    setSavedSnapshot({ headerContent: headerData?.headerContent || '', variableValues, footerVariables })
                } else {
                    if (respo.message === "tokenExpired") {
                        authCtx.logout()
                    } else {
                        toast.error(respo?.message || `Couldn't save. Please try again.`)
                    }
                }
            }
            setIsUpdatingFields(false)
        }

        setTemplateVariableForCampaign()

    };

    const getTemplateVariableForCampaign = async () => {
        // The saved varData never records what kind of header it was (only
        // its position/value) — headerType has to come from the template's
        // own metadata (template?.headerType), not be guessed here. This
        // previously hardcoded 'image' unconditionally, so reopening a
        // saved text or video header silently showed it as an image header.
        // The position check is also loosened to compare loosely rather
        // than strictly against the string '1', since it's not certain
        // whether the backend returns position as a number or a string.
        function extractTemplateSections(respo) {
            let headerContent = '';
            let body = {};
            let footer = {};

            respo.forEach(item => {
                const { varType, position, value } = item;

                if (varType === 'header' && String(position) === '1') {
                    headerContent = value;
                } else if (varType === 'body') {
                    body[`{{${position}}}`] = value;
                } else if (varType === 'footer' && String(position) === '1') {
                    footer[position] = value;
                }
            });

            return { headerContent, body, footer };
        }


        const response = await fetch(BASE_URL_API + "getTemplateVariableForCampaign",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    campaignIdentifier: campaignIdentifier,
                    templateIdentifier: template?.templateIdentifier
                })
            })

        if (!response.ok) {
            toast.error(`Couldn't load saved values for this template.`)
        } else {
            const respo = await response.json()
            if (respo.status === "success") {
                const ets = extractTemplateSections(respo?.response || [])
                setHeaderData({ headerType: template?.headerType, headerContent: ets.headerContent })
                setVariableValues(ets.body)
                setFooterVariables(ets.footer)
                setSavedSnapshot({ headerContent: ets.headerContent, variableValues: ets.body, footerVariables: ets.footer })
            } else {
                if (respo.message === "tokenExpired") {
                    authCtx.logout()
                } else if (respo.message) {
                    // Not every campaign/template pairing has saved values yet
                    // (a brand-new selection) — only surface a message if the
                    // backend actually sent one, rather than erroring on the
                    // normal "nothing saved yet" case.
                    toast.error(respo.message)
                }
            }
        }
    }

    const openImageSelector = async () => {
        setIsContentSelectorOpen(true)
    }

    const closeContentSelector = async () => {
        setIsContentSelectorOpen(false)
    }

    useEffect(() => {
        getTemplateVariableForCampaign()
    }, [template?.templateIdentifier])

    useEffect(() => {
        setTemplateErrors(validateTemplateVars(buildVarData()))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headerData, variableValues, footerVariables, selectedTemplate])

    const selectImageHandler = (url) => {
        setHeaderData({ ...headerData, headerContent: url })
        setIsContentSelectorOpen(false)
    }

    const objectsEqual = (a = {}, b = {}) => {
        const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
        for (const key of keys) {
            if ((a[key] || "") !== (b[key] || "")) return false;
        }
        return true;
    };

    const isDirty = (headerData?.headerContent || "") !== (savedSnapshot.headerContent || "")
        || !objectsEqual(variableValues, savedSnapshot.variableValues)
        || !objectsEqual(footerVariables, savedSnapshot.footerVariables);

    if (!visible || !selectedTemplate) return null;

    return (
        <div className={styles.previewOverlay} onClick={handleOverlayClick}>
            <div
                className={styles.previewContent}
                onClick={handleContentClick}
                style={mobileHeaderHeight ? { '--mobile-header-height': `${mobileHeaderHeight}px` } : undefined}
            >
                {/* Plain, minimal close button (no circle/shadow — that mobile
                    treatment looked inconsistent with this same button's own
                    desktop style and every other close button in this app,
                    e.g. the Audience preview drawer's). Fixed positioning on
                    mobile only, via CSS, so it stays reachable. */}
                <button className={styles.closeBtn} onClick={onClose}>
                    <AiOutlineClose />
                </button>
                {/* One real, non-scrolling header block (mobile only) instead of
                    a sticky element mixed into scrolling content — that mix
                    was producing visual glitches where later sections could
                    render as if above this one. Title + status live inside
                    ONE fixed container (not two independently-fixed pieces
                    with a guessed pixel offset between them, which is what
                    let the content start scrolling out from underneath —
                    its actual rendered height is measured below and used to
                    push .previewContent's content down by exactly that much,
                    so it can't drift out of sync again as content changes
                    (e.g. the error list expanding/collapsing). */}
                <div className={styles.mobileFixedHeader} ref={mobileHeaderRef}>
                    <div className={styles.mobileHeaderBar}>
                        <h3 className={styles.title}>Fill Variables</h3>
                    </div>
                    <div className={styles.mobileStatusBar}>
                        {templateErrors?.status !== "success" ?
                            <div className={styles.errorWrapper}>
                                <div className={styles.noParamsReady} onClick={() => setIsErrorOpen(!isErrorOpen)} >
                                    {templateErrors?.errors?.length} error found!
                                    {isErrorOpen === true ?
                                        <FaChevronDown size={14} />
                                        :
                                        <FaChevronUp size={14} />
                                    }
                                </div>
                                {isErrorOpen === true &&
                                    <div>
                                        <ul>
                                            {templateErrors?.errors?.map((itemErr, indexErr) => (
                                                <li key={indexErr} >
                                                    <div className={styles.errTxt}>
                                                        {itemErr.error}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                }
                            </div>
                            :
                            <div className={styles.errorWrapper}>
                                <div className={styles.allParamsReady}>
                                    <FaCheck color="green" />
                                    All parameters are ready!
                                </div>
                            </div>
                        }
                    </div>
                </div>
                {isContentSelectorOpen && <ContentSelector isOpen={isContentSelectorOpen} onClose={closeContentSelector} onSelect={selectImageHandler} /> }

                <div className={styles.layout}>
                    {/* Left Column: Fill Variables */}
                    <div className={styles.leftColumn}>
                        {/* Desktop keeps title + status inline here (hidden on
                            mobile, where the bars above take over). */}
                        <div className={styles.leftColumnHeader}>
                            <h3 className={styles.title}>Fill Variables</h3>
                            <div>
                                {templateErrors?.status !== "success" ?
                                    <div className={styles.errorWrapper}>
                                        <div className={styles.noParamsReady} onClick={() => setIsErrorOpen(!isErrorOpen)} >
                                            {templateErrors?.errors?.length} error found!
                                            {isErrorOpen === true ?
                                                <FaChevronDown size={14} />
                                                :
                                                <FaChevronUp size={14} />
                                            }
                                        </div>
                                        {isErrorOpen === true &&
                                            <div>
                                                <ul>
                                                    {templateErrors?.errors?.map((itemErr, indexErr) => (
                                                        <li key={indexErr} >
                                                            <div className={styles.errTxt}>
                                                                {itemErr.error}
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        }
                                    </div>
                                    :
                                    <div className={styles.errorWrapper}>
                                        <div className={styles.allParamsReady}>
                                            <FaCheck color="green" />
                                            All parameters are ready!
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                        {selectedTemplate?.headerType != null &&
                            <div>
                                <div className={styles.sectionTitle}>
                                    Header
                                </div>
                                <div>
                                    {headerData?.headerType === "text" &&
                                        <div className={styles.headerInput}>
                                            <input placeholder="Enter header text"
                                                value={headerData?.headerContent || ''}
                                                onChange={(e) => setHeaderData({ ...headerData, headerContent: e.target.value })}
                                            />
                                        </div>
                                    }
                                    {headerData?.headerType === "image" &&
                                        <div className={styles.headerInput}>
                                            <input placeholder="Please enter image URL"
                                                value={headerData?.headerContent}
                                                onChange={(e) => setHeaderData({ ...headerData, headerContent: e.target.value })}
                                                readOnly
                                            />
                                            <button onClick={() => openImageSelector()} >Select</button>
                                        </div>
                                    }
                                    {headerData?.headerType === "video" &&
                                        <div className={styles.headerInput}>
                                            <input placeholder="Enter video URL"
                                                value={headerData?.headerContent || ''}
                                                onChange={(e) => setHeaderData({ ...headerData, headerContent: e.target.value })}
                                            />
                                        </div>
                                    }
                                </div>
                            </div>
                        }

                        <div>
                            <div className={styles.sectionTitle}>
                                Body
                            </div>

                            <div className={styles.variablesContainer}>
                                {variables.length === 0 ? (
                                    <p>No variables found in the template.</p>
                                ) : (
                                    variables.map((item, index) => (
                                        <div className={styles.variableRow} key={index}>
                                            <div className={styles.dropdown} >
                                                <select onChange={(e) => variableSelectorHandler(item, e.target.value)}>
                                                    <option>Enter</option>
                                                    {predefinedParams?.map((itemParam, indexParam) => (
                                                        <option key={indexParam} value={itemParam?.identifier}>
                                                            {itemParam?.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className={styles.inputRow}>
                                                <input
                                                    className={styles.variableInput}
                                                    placeholder={`Enter value for ${item}`}
                                                    value={variableValues[item] || ""}
                                                    onChange={(e) => handleVariableChange(item, e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                        {(selectedTemplate?.footer_var_1 != null || selectedTemplate?.footer_var_2 != null) &&
                        <div>
                            <div className={styles.sectionTitle}>Footer</div>
                            {selectedTemplate?.footer_var_1 !== null &&
                                <div className={styles.variableRow}>
                                    <div className={styles.dropdown} >
                                        <select onChange={(e) => footerSelectorHandler(1, e.target.value)}>
                                            <option>Enter</option>
                                            {predefinedParams?.map((itemParam, indexParam) => (
                                                <option key={indexParam} value={itemParam?.identifier}>
                                                    {itemParam?.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className={styles.inputRow}>
                                        <input
                                            className={styles.variableInput}
                                            placeholder={`Enter value for`}
                                            value={footerVariables[1] || ""}
                                            onChange={(e) => handleFooterVariableChange(1, e.target.value)}
                                        />
                                    </div>
                                </div>
                            }

                            {selectedTemplate?.footer_var_2 !== null &&
                                <div className={styles.variableRow}>
                                    <div className={styles.dropdown} >
                                        <select onChange={(e) => footerSelectorHandler(2, e.target.value)}>
                                            <option>Enter</option>
                                            {predefinedParams?.map((itemParam, indexParam) => (
                                                <option key={indexParam} value={itemParam?.identifier}>
                                                    {itemParam?.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className={styles.inputRow}>
                                        <input
                                            className={styles.variableInput}
                                            placeholder={`Enter value for`}
                                            value={footerVariables[2] || ""}
                                            onChange={(e) => handleFooterVariableChange(2, e.target.value)}
                                        />
                                    </div>
                                </div>
                            }

                        </div>
                        }
                        <div className={styles.actionWrapper}>
                            {isUpdatingFields === true ?
                                <button className={styles.updateBtn} disabled >
                                    Updating...
                                </button>
                                :
                                <button className={styles.updateBtn} onClick={() => submissionHandler()} disabled={!isDirty} >
                                    Update Field
                                </button>
                            }
                            <button
                                className={styles.selectButton}
                                onClick={handleSelectTemplate}
                                disabled={isDirty || templateErrors?.status !== "success"}
                            >
                                <MdOutlineMessage />
                                Select Template
                            </button>
                        </div>

                    </div>

                    {/* Right Column: Preview */}
                    <div className={styles.rightColumn}>
                        <h3 className={`${styles.title} ${styles.rightColumnTitle}`}>Message Preview</h3>
                        <TemplatePreviewWhatsApp headerData={headerData} previewText={previewText} />



                    </div>
                </div>
            </div>
        </div>
    );
};

export default TemplatePreview;
